/**
 * Resizes an image File to fit within maxDim×maxDim pixels,
 * encodes as JPEG at the given quality, and returns a data URL.
 * If the image is already small enough, it is still re-encoded at the
 * target quality to keep file sizes consistent.
 */
export function resizeImageToDataUrl(
  file: File,
  maxDim = 256,
  quality = 0.82,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.onload = (ev) => {
      const img = new Image();
      img.onerror = () => reject(new Error('Failed to decode image'));
      img.onload = () => {
        const { naturalWidth: w, naturalHeight: h } = img;
        const scale = Math.min(1, maxDim / Math.max(w, h));
        const tw = Math.round(w * scale);
        const th = Math.round(h * scale);

        const canvas = document.createElement('canvas');
        canvas.width  = tw;
        canvas.height = th;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('Canvas not supported')); return; }
        ctx.drawImage(img, 0, 0, tw, th);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = ev.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
}
